//----------------------------------------------------------------------------------------------------------------------
// User Input Manager
//----------------------------------------------------------------------------------------------------------------------

// Interfaces
import type { GamepadInputState, InputDevice, KeyboardInputState, MouseInputState } from '../interfaces/input.ts';
import type { Disposable } from '../interfaces/lifecycle.ts';
import type { LoggerInterface } from '../interfaces/logger.ts';

// Classes
import { GameEventBus } from '../classes/eventBus.ts';

// Utils
import { LoggingUtility, SAGELogger } from '../utils/logger.ts';

// Resource Access
import { KeyboardInputPlugin } from '../classes/input/keyboard.ts';
import { MouseInputPlugin } from '../classes/input/mouse.ts';
import { GamepadInputPlugin } from '../classes/input/gamepad.ts';

//----------------------------------------------------------------------------------------------------------------------

/**
 * Manager for handling user input from various devices (keyboard, mouse, gamepad)
 */
export class UserInputManager implements Disposable
{
    private _eventBus : GameEventBus;

    private _keyboardRA : KeyboardInputPlugin;
    private _mouseRA : MouseInputPlugin;
    private _gamepadRA : GamepadInputPlugin;

    /** Logger instance */
    private _log : LoggerInterface;

    //------------------------------------------------------------------------------------------------------------------

    /**
     * Create a new UserInputManager.
     *
     * @param eventBus
     * @param canvas - The DOM element to attach input listeners to (used by mouse input)
     * @param logger
     */
    constructor(
        eventBus : GameEventBus,
        canvas : HTMLElement,
        logger ?: LoggingUtility
    )
    {
        this._eventBus = eventBus;

        // Initialize logger
        this._log = logger?.getLogger('UserInputManager') || new SAGELogger('UserInputManager');

        this._log.info('Initializing UserInputManager');

        // Initialize resource access classes
        this._log.debug('Initializing input resource access classes');
        this._keyboardRA = new KeyboardInputPlugin();
        this._mouseRA = new MouseInputPlugin(canvas);
        this._gamepadRA = new GamepadInputPlugin();

        // Register callbacks
        this._log.debug('Registering input event callbacks');
        this._keyboardRA.onDeviceConnected(this._publishDeviceConnected.bind(this));
        this._keyboardRA.onInputChanged(this._publishInputChanged.bind(this));
        this._mouseRA.onDeviceConnected(this._publishDeviceConnected.bind(this));
        this._mouseRA.onInputChanged(this._publishInputChanged.bind(this));
        this._gamepadRA.onDeviceConnected(this._publishDeviceConnected.bind(this));
        this._gamepadRA.onDeviceDisconnected(this._publishDeviceDisconnected.bind(this));
        this._gamepadRA.onInputChanged(this._publishInputChanged.bind(this));

        this._log.info('UserInputManager initialized successfully');
    }

    //------------------------------------------------------------------------------------------------------------------
    // Private methods
    //------------------------------------------------------------------------------------------------------------------

    /**
     * Publish device connected event to the event bus
     */
    private _publishDeviceConnected(device : InputDevice) : void
    {
        this._log.debug(`Device connected: ${ device.id } (${ device.name })`);
        this._eventBus.publish({ type: 'input:device:connected', payload: { device } });
    }

    /**
     * Publish device disconnected event to the event bus
     */
    private _publishDeviceDisconnected(device : InputDevice) : void
    {
        this._log.debug(`Device disconnected: ${ device.id } (${ device.name })`);
        this._eventBus.publish({ type: 'input:device:disconnected', payload: { device } });
    }

    /**
     * Publish input changed event to the event bus, used by all device types
     */
    private _publishInputChanged(
        device : InputDevice,
        state : KeyboardInputState | MouseInputState | GamepadInputState
    ) : void
    {
        this._log.trace(`Input changed: ${ device.id }`, state);
        this._eventBus.publish({
            type: 'input:changed',
            payload: {
                deviceId: device.id,
                device,
                state,
            },
        });
    }

    //------------------------------------------------------------------------------------------------------------------
    // Internal methods
    //------------------------------------------------------------------------------------------------------------------

    /**
     * Tears down the input manager and clean up event listeners
     */
    public $teardown() : Promise<void>
    {
        this._log.info('Tearing down UserInputManager');

        // Cleanup all resource access instances
        this._log.debug('Cleaning up input resource access instances');

        // Using Promise.all to handle multiple async teardowns
        return Promise.all([
            this._keyboardRA.$teardown(),
            this._mouseRA.$teardown(),
            this._gamepadRA.$teardown(),
        ]).then(() =>
        {
            this._log.info('UserInputManager torn down successfully');
        });
    }

    //------------------------------------------------------------------------------------------------------------------
    // Public methods
    //------------------------------------------------------------------------------------------------------------------

    /**
     * Get all connected input devices (keyboard, mouse, and any gamepads).
     */
    public listDevices() : InputDevice[]
    {
        this._log.debug('Getting all connected input devices');

        const devices : InputDevice[] = [];

        // Add keyboard and mouse
        devices.push(this._keyboardRA.getDevice());
        devices.push(this._mouseRA.getDevice());

        // Add all gamepads
        devices.push(...this._gamepadRA.getDevices());

        this._log.debug(`Found ${ devices.length } connected devices`);
        return devices;
    }

    /**
     * Get a specific input device by ID.
     * Uses device ID prefix (keyboard-, mouse-, gamepad-) for fast lookup.
     *
     * @param deviceId
     */
    public getDevice(deviceId : string) : InputDevice | null
    {
        this._log.debug(`Getting device: ${ deviceId }`);

        // Fast path for known device prefixes
        if(deviceId.startsWith('keyboard-'))
        {
            return this._keyboardRA.getDevice();
        }

        if(deviceId.startsWith('mouse-'))
        {
            return this._mouseRA.getDevice();
        }

        if(deviceId.startsWith('gamepad-'))
        {
            const index = parseInt(deviceId.split('-')[1], 10);
            return this._gamepadRA.getDevice(index);
        }

        // Slow path fallback - iterate through all devices
        const device = this.listDevices().find((dev) => dev.id === deviceId);
        if(device)
        {
            return device;
        }

        this._log.warn(`Device not found: ${ deviceId }`);
        return null;
    }

    /**
     * Poll for gamepad state updates - call this in your game loop
     */
    public pollGamepads() : void
    {
        this._gamepadRA.pollGamepads();
    }
}

//----------------------------------------------------------------------------------------------------------------------

